import {
  AuthModalVariant,
  GetAuthModalStateDocument,
  useGetAuthModalStateQuery,
} from '@kijiji/generated/graphql-types'
import LoginToFinanceInquiryIcon from '@kijiji/icons/src/icons/IllustrationModalRegisterAlmostDone'
import SignInToMessageIcon from '@kijiji/icons/src/icons/IllustrationModalSignInMessage'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { useTheme } from 'styled-components'

import { LinkCustom } from '@/components/shared/link-custom/LinkCustom'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { LocalStorageRegistry } from '@/constants/localStorageRegistry'
import {
  AuthModalContent,
  AuthModalFooter,
  AuthModalIconContainer,
  AuthModalPanel,
  AuthModalRegisterMessage,
  AuthModalTitle,
} from '@/features/auth/components/auth-modal/styled'
import { useSignin } from '@/features/auth/hooks/useSignin'
import { type GuestContactDetails } from '@/features/listing/components/reply-to-seller/conversations/R2SForm'
import { getR2SSuccessMessage } from '@/features/listing/utils/reply-to-seller/getR2SSuccessMessage'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { BodyText } from '@/ui/atoms/body-text/BodyText'
import { Button } from '@/ui/atoms/button'
import { Spacing } from '@/ui/atoms/spacing/Spacing'
import { Modal } from '@/ui/molecules/modal/Modal'
import { SystemMessage } from '@/ui/molecules/system-message/SystemMessage'
import signInImage from '~/public/next-assets/images/sign-in-modal-icon.png'

export const AuthModal = () => {
  const { data: { authModalState } = {}, client } = useGetAuthModalStateQuery()

  const { colors, spacing } = useTheme()

  const { t } = useTranslation([
    TRANSLATION_KEYS.COMMON,
    TRANSLATION_KEYS.ROUTES,
    TRANSLATION_KEYS.FAVOURITES,
    TRANSLATION_KEYS.R2S,
  ])

  const [guestContactDetails] = useLocalStorage<GuestContactDetails>(
    LocalStorageRegistry.R2S_GUEST_CONTACT,
    {
      name: '',
      email: '',
    }
  )

  const isModalOpen = authModalState?.isModalOpen || false
  const callbackUrl = authModalState?.callbackUrl || undefined
  const variant = authModalState?.modalVariant
  const categoryId = authModalState?.categoryId ?? null

  let titleText,
    bodyText = ''
  let illustration = null
  let successMessage = null

  switch (variant) {
    case AuthModalVariant.GuestMessage: {
      titleText = t('r2s:guest_message_sign_in.title')
      bodyText = t('r2s:guest_message_sign_in.description')
      illustration = <SignInToMessageIcon aria-hidden />
      successMessage =
        categoryId && getR2SSuccessMessage(t, categoryId, true, guestContactDetails.email)
      break
    }
    case AuthModalVariant.LoginToFavourite: {
      titleText = t('favourites:favourites.login_modal.title')
      bodyText = t('favourites:favourites.login_modal.body_text')
      illustration = <Image aria-hidden="true" src={signInImage} alt="" width={210} />
      break
    }
    case AuthModalVariant.LoginToMessage: {
      titleText = t('r2s:login_to_message.title')
      bodyText = t('r2s:login_to_message.description')
      illustration = <SignInToMessageIcon aria-hidden />
      break
    }
    case AuthModalVariant.LoginToRequestViewing: {
      titleText = t('r2s:login_to_request_viewing.title')
      illustration = <Image aria-hidden="true" src={signInImage} alt="" width={210} />
      break
    }
    case AuthModalVariant.LoginToFinanceInquiry: {
      titleText = t('r2s:login_to_finance_inquiry.title')
      illustration = <LoginToFinanceInquiryIcon aria-hidden />
      break
    }
    default: {
      // Default no context login prompt should go here
      bodyText = t('favourites:favourites.login_modal.body_text')
      titleText = t('favourites:favourites.login_modal.title')
      illustration = <Image aria-hidden="true" src={signInImage} alt="" width={210} />
    }
  }

  const registerHref = t('routes:register.href')

  const [goToSignIn, setGoToSignIn] = useState(false)
  useSignin({ callbackUrl: callbackUrl, goToSignIn: goToSignIn })

  const handleAuthClick = () => {
    setGoToSignIn(true)
  }

  const handleCloseAuthModal = () => {
    client.cache.writeQuery({
      query: GetAuthModalStateDocument,
      data: {
        authModalState: {
          isModalOpen: false,
          modalVariant: null,
          callbackUrl: null,
        },
      },
    })
  }

  return (
    <Modal
      data-testid="sign-in-favourite-modal"
      id="sign-in-modal"
      isOpen={isModalOpen}
      onCancel={handleCloseAuthModal}
      label={titleText}
      hasCloseButton
      closeButtonLabel={t('common:modals.login.labels.close_login')}
      width={{ small: '100vw', medium: '40rem' }}
    >
      <AuthModalPanel>
        {successMessage ? (
          <Spacing mTop={spacing.husky}>
            <SystemMessage
              description={
                <BodyText color={colors.grey.light1}>{successMessage.description}</BodyText>
              }
              hideCloseButton
              title={successMessage.title}
              variation="success"
            />
          </Spacing>
        ) : null}

        <AuthModalIconContainer>{illustration}</AuthModalIconContainer>

        <AuthModalTitle>{titleText}</AuthModalTitle>
        {bodyText ? <AuthModalContent>{bodyText}</AuthModalContent> : null}
        <AuthModalFooter>
          <Button
            data-testid="sign-in-modal-button"
            isFullWidth
            onClick={handleAuthClick}
            variant="primary"
          >
            {t('common:modals.login.labels.sign_in_button')}
          </Button>
          <AuthModalRegisterMessage>
            {t('common:modals.login.labels.create_account_title')}{' '}
            <LinkCustom href={registerHref} variant="secondary">
              {t('common:modals.login.labels.create_account_link_text')}
            </LinkCustom>
          </AuthModalRegisterMessage>
        </AuthModalFooter>
      </AuthModalPanel>
    </Modal>
  )
}
